import React, { useState, useEffect } from "react";
import { UserCheck, Layers, RefreshCw } from "lucide-react";
import { Modal } from "./Modal";
import type { WorkspaceItem } from "../../types";
import type { B2bMembershipRequestItem } from "@/src/api/types.gen";

export interface ApproveRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: B2bMembershipRequestItem | null;
  workspaces: WorkspaceItem[];
  isProcessing: boolean;
  onApprove: (userId: number, workspaceId?: number) => Promise<void>;
}

export const ApproveRequestModal: React.FC<ApproveRequestModalProps> = ({
  isOpen,
  onClose,
  request,
  workspaces = [],
  isProcessing,
  onApprove,
}) => {
  const defaultWs = workspaces.find((w) => w.is_default);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | "">("");

  useEffect(() => {
    if (defaultWs) {
      setSelectedWorkspaceId(defaultWs.id);
    } else {
      setSelectedWorkspaceId("");
    }
  }, [defaultWs, isOpen]);

  if (!request) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onApprove(
      request.user_id,
      selectedWorkspaceId ? Number(selectedWorkspaceId) : undefined
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Approve Membership Request"
      icon={<UserCheck className="w-5 h-5 text-emerald-600" />}
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {/* Requester Summary */}
        <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-900 text-sm">
              {request.name || "New Learner"}
            </span>
            <span className="px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold uppercase rounded-full">
              Pending Approval
            </span>
          </div>
          <div className="text-slate-500 font-mono text-[11px]">{request.email}</div>
          <div className="flex items-center gap-3 pt-1 border-t border-slate-200/60 text-slate-600">
            <span>Department: <strong>{request.department || "General"}</strong></span>
            {request.job_title && (
              <span>Title: <strong>{request.job_title}</strong></span>
            )}
          </div>
        </div>

        {/* Workspace Selection */}
        <div>
          <label className="block font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-indigo-600" />
              Assign to Workspace *
            </span>
            <span className="text-[10px] text-slate-400">Determines course access</span>
          </label>
          <select
            value={selectedWorkspaceId}
            onChange={(e) => setSelectedWorkspaceId(e.target.value ? Number(e.target.value) : "")}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          >
            <option value="">General (Default Workspace)</option>
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {ws.name} ({ws.is_default ? "Default" : `/${ws.slug}`})
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
            The member will only be able to view and access courses assigned to this workspace. You can reassign or add them to additional workspaces later.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isProcessing}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50 flex items-center gap-1.5"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Approving...</span>
              </>
            ) : (
              <>
                <UserCheck className="w-3.5 h-3.5" />
                <span>Approve & Grant Access</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
